import {
  createRequestEnvelope,
  finalizeRequestEnvelope,
  serializeEnvelope,
} from "./libs/requestEnvelope";

// In-memory short JWT store
let _shortJwt = null;
// Track refresh status to prevent parallel refreshes
let _refreshPromise = null;
export function setShortJwt(token) {
  _shortJwt = token;
}
export function getShortJwt() {
  return _shortJwt;
}

// Cache for loaded backgrounds
const backgroundCache = new Map();

export class HTTPError extends Error {
  constructor(response, data = {}) {
    const errorMessage =
      typeof data === "object" && (data?.detail || data?.error)
        ? `${response.status}: ${data.detail || data.error}`
        : `${response.status} ${response.statusText || ""}`;
    super(errorMessage);
    this.name = "HTTPError";
    this.data = data;
    this.status = response.status;
    // Populated by request() when available. Lets callers surface the same
    // request-side context (payload size, timing, request id) regardless of
    // whether the error came from a clean HTTP response or a CORS / network
    // failure where no response was received.
    this.envelope = null;
  }
}

// Thrown when fetch() itself rejects (CORS, DNS, network) — distinct from
// HTTPError, which represents a response-with-non-2xx-status. Carries the
// envelope so callers always have something to log/show even when the
// browser never saw a response.
export class NetworkError extends Error {
  constructor(envelope, cause) {
    const causeMsg = cause?.message ? `: ${cause.message}` : "";
    super(`Network request failed${causeMsg}`);
    this.name = "NetworkError";
    this.envelope = envelope;
    this.cause = cause;
  }
}

// ---------------------------------------------------------------------------
// Upload retry policy
// ---------------------------------------------------------------------------

/** Backoff between upload attempts: first retry after 1s, second after 3s. */
export const UPLOAD_RETRY_DELAYS_MS = [1000, 3000];

/**
 * Transient failures worth retrying: the browser never got a response
 * (NetworkError), or an infrastructure-tier 5xx (pod restart, ingress
 * buffering failure — the 2026-07-04 "500 after 5s" upload report). 4xx are
 * deterministic (auth, size, type) and retrying them just repeats the answer.
 */
export function isRetryableUploadError(err) {
  if (err instanceof NetworkError) return true;
  if (err instanceof HTTPError && err.status >= 500) return true;
  return false;
}

/**
 * Run an upload attempt with retries on transient failures. Safe for
 * /api/v3/uploads because the endpoint deduplicates by content hash — a
 * retry after an attempt that actually stored the file is a no-op that
 * returns the same {id, url}.
 */
export async function withUploadRetries(fn, {delays = UPLOAD_RETRY_DELAYS_MS, onRetry} = {}) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= delays.length || !isRetryableUploadError(err)) throw err;
      onRetry?.(err, attempt + 1, delays[attempt]);
      await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
    }
  }
}

/** Time between two status requests of a server video job. */
export const VIDEO_JOB_POLL_MS = 2000;
/** Status requests that can fail in sequence before a video export stops. */
const VIDEO_JOB_MAX_FAILED_POLLS = 5;

export async function request(url, options = {}) {
  const {responseType, ...fetchOptions} = options;
  const isFormData = typeof FormData !== "undefined" && options?.body instanceof FormData;

  const initialHeaders = isFormData ? {} : {"Content-Type": "application/json;charset=utf-8"};

  const envelope = createRequestEnvelope({
    url,
    method: options?.method || "GET",
    body: options?.body,
  });

  const makeFetch = async (opts = {}) => {
    const authHeader = _shortJwt ? {Authorization: `Bearer ${_shortJwt}`} : {};
    const requestIdHeader = {"X-Request-ID": envelope.requestId};

    const mergedHeaders = {
      ...initialHeaders,
      ...authHeader,
      ...requestIdHeader,
      ...options.headers,
      ...opts.headers,
    };

    return fetch(url, {
      ...fetchOptions,
      ...opts,
      headers: mergedHeaders,
    });
  };

  let response;
  let data;

  try {
    response = await makeFetch();

    if (response.status === 401 && _shortJwt) {
      if (!_refreshPromise) {
        _refreshPromise = fetch("/auth/issue", {
          method: "POST",
          credentials: "include",
          headers: {Accept: "application/json"},
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((json) => {
            if (json?.token) {
              setShortJwt(json.token);
              return json.token;
            }
            // Failed refresh – clear token
            setShortJwt(null);
            return null;
          })
          .catch((err) => {
            console.warn("Refresh token error", err);
            setShortJwt(null);
            return null;
          })
          .finally(() => {
            _refreshPromise = null;
          });
      }
      const newToken = await _refreshPromise;
      if (newToken) {
        // Retry original request once with new token. No infinite loop:
        // this branch is reached only via the 401-guarded `if` above and
        // doesn't recurse into request() — at most one retry per call.
        response = await makeFetch();
      }
    }

    const contentType = response.headers.get("content-type");
    if (response.ok && responseType === "download") {
      data = {blob: await response.blob(), filename: response.headers.get("content-disposition")?.match(/filename="([^"/\\]+)"/)?.[1]};
    } else if (response.ok && responseType === "blob") {
      data = await response.blob();
    } else if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      finalizeRequestEnvelope(envelope, {response});
      const httpError = new HTTPError(response, data);
      httpError.envelope = envelope;
      if (!options?.suppressErrorLog) {
        console.error("[api] HTTP error", serializeEnvelope(envelope, {data}));
      }
      throw httpError;
    }
    finalizeRequestEnvelope(envelope, {response});
    return data;
  } catch (error) {
    if (error instanceof HTTPError) {
      // Already logged (or intentionally suppressed) above; re-throw untouched.
      throw error;
    }
    // fetch() rejected before any response — CORS strip, DNS, ECONNREFUSED,
    // abort. The envelope is the only signal we'll ever have for this; log
    // it and wrap so callers get a typed error with attached context.
    finalizeRequestEnvelope(envelope, {error});
    if (!options?.suppressErrorLog) {
      console.error("[api] Network error", serializeEnvelope(envelope));
    }
    throw new NetworkError(envelope, error);
  }
}

export function mapScenesResponse(hashes, response) {
  if (
    !response ||
    !response.scenes ||
    typeof response.scenes !== "object" ||
    Array.isArray(response.scenes)
  ) {
    throw new Error("Invalid scene-load response: scenes missing");
  }
  return hashes.map((hash) => response.scenes[hash] ?? null);
}

export const API = {
  fetchFeatureFlags(include_hidden = false) {
    const params = new URLSearchParams();
    params.append("include_hidden", include_hidden.toString());

    return request(`/api/flags?${params.toString()}`, {
      method: "GET",
    });
  },

  createFeatureFlag(payload) {
    return request(`/api/flags`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateFeatureFlag(key, role, payload) {
    return request(`/api/flags/${key}/${role}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  deleteFeatureFlag(key) {
    return request(`/api/flags/${key}`, {
      method: "DELETE",
    });
  },

  fetchUserRoles() {
    return request(`/api/admin/users`, {
      method: "GET",
    });
  },

  updateUserRole(email, role) {
    return request(`/api/admin/users/${encodeURIComponent(email)}/role`, {
      method: "PUT",
      body: JSON.stringify({role}),
    });
  },

  deleteUserRole(email) {
    return request(`/api/admin/users/${encodeURIComponent(email)}/role`, {
      method: "DELETE",
    });
  },

  /** Superadmin only: view the app as `role`, or stop with null (src/server/lib/view-as.ts). */
  setViewAsRole(role) {
    return request(`/api/auth/view-as`, {
      method: "POST",
      body: JSON.stringify({role}),
    });
  },

  fetchAnalyticsSummary(days = 365) {
    return request(`/analytics/summary?days=${days}`, {
      method: "GET",
    });
  },

  fetchAnalyticsDaily(days = 365) {
    return request(`/analytics/daily?days=${days}`, {
      method: "GET",
    });
  },

  fetchArtifactStats() {
    return request("/analytics/artifacts", {
      method: "GET",
    });
  },

  refreshArtifactStats() {
    return request("/analytics/artifacts/refresh", {
      method: "POST",
    });
  },

  fetchBackgrounds(channel, type) {
    return request(`/tiles/get_tilesets?prefix=Neurobaker/Backgrounds/${channel}/${type}`, {
      method: "GET",
    });
  },

  /**
   * v3 background catalog (issue #528, 2a). One response per variation with the
   * CDN image URL, thumbnail, dimensions, style markers, and per-template crop
   * transforms already resolved for `product` (product → default → cover-fit).
   * Replaces the get_tilesets + scene/load/v2 two-hop. Returns {backgrounds: [...]}.
   */
  fetchBackgroundsCatalog(channel, type, product) {
    const params = new URLSearchParams({channel, type});
    if (product) params.set("product", product);
    return request(`/api/v3/backgrounds?${params}`, {method: "GET"});
  },

  /**
   * Server-resolved default-scene cascade (issue #528, 1c). Returns
   * {scene_data, exact_row, layers} — the merged override cascade, the raw row
   * for the exact key, and the matched cascade layers (least → most specific).
   * Replaces the client-side resolveAllOverrides + mergeScenes.
   *
   * Plus {product_default_scene, product_default_hash} (issue #567 A1): the
   * products.js default scene for the key, already loaded from the scene store,
   * so a route whose default comes from that registry rather than an override row
   * doesn't need `POST /scene/load/v2`. Null when the registry defines none.
   */
  fetchResolvedDefaultScene({channel, product, type, set, layout, background, variant} = {}) {
    const params = new URLSearchParams({channel, type});
    if (product) params.set("product", product);
    if (set) params.set("set", set);
    if (layout) params.set("layout", layout);
    if (background) params.set("background", background);
    if (variant) params.set("variant", variant);
    return request(`/api/v3/default-scenes/resolved?${params}`, {
      method: "GET",
      suppressErrorLog: true,
    });
  },

  /**
   * The /default-scenes/resolved response for many backgrounds of one route,
   * as {results: {[background]: response}}. The background selector draws each
   * tile with its default crop from it.
   */
  fetchResolvedDefaultScenesBatch({channel, product, type, set, layout, variant, backgrounds}) {
    const params = new URLSearchParams({channel, type, backgrounds: backgrounds.join(",")});
    if (product) params.set("product", product);
    if (set) params.set("set", set);
    if (layout) params.set("layout", layout);
    if (variant) params.set("variant", variant);
    return request(`/api/v3/default-scenes/resolved-batch?${params}`, {
      method: "GET",
      suppressErrorLog: true,
    });
  },

  /**
   * Natural pixel size of background images, as {sizes: {[url]: {width,
   * height} | null}}. The crop transform is in these pixels. thumbnailUrls
   * requests small WebP icons for sources that have no catalog thumbnail.
   */
  fetchBgImageSizes(urls, thumbnailUrls = []) {
    return request(`/api/v3/backgrounds/image-sizes`, {
      method: "POST",
      body: JSON.stringify({urls, ...(thumbnailUrls.length ? {thumbnailUrls} : {})}),
      suppressErrorLog: true,
    });
  },

  /**
   * The same cascade resolved for every product of a (channel, type) — issue
   * #567 A2. Returns {scenes: {[productCode]: sceneData}, bg_options,
   * all_products_bg_options} for the ALL-products aggregate view, which is the
   * only consumer that needs more than one product's defaults (previously the
   * reason the whole override list shipped to the browser). The two bg maps back
   * the per-product background carousel.
   */
  fetchResolvedDefaultScenesAll({channel, type, set, layout, background} = {}) {
    const params = new URLSearchParams({channel, type});
    if (set) params.set("set", set);
    if (layout) params.set("layout", layout);
    if (background) params.set("background", background);
    return request(`/api/v3/default-scenes/resolved-all?${params}`, {
      method: "GET",
      suppressErrorLog: true,
    });
  },

  /**
   * The editable background list (channel + type), already in display order.
   * Anonymous callers get the published global list; a signed-in caller gets
   * their own arrangement layered on top of it. Each entry's `variation` is its
   * POSITION — see /api/v3/bg-list. `scope: "global"` reads the published list
   * even for a signed-in user, which is what the admin panel compares against.
   */
  fetchBgList(channel, type, product, scope) {
    const params = new URLSearchParams({channel, type});
    if (product) params.set("product", product);
    if (scope) params.set("scope", scope);
    return request(`/api/v3/bg-list?${params}`, {method: "GET"});
  },

  /** Save the caller's own arrangement. Returns the resolved list. */
  putBgList({channel, type, product, entries, removed}) {
    return request(`/api/v3/bg-list`, {
      method: "PUT",
      body: JSON.stringify({channel, type, product, entries, removed}),
    });
  },

  /** Drop the caller's arrangement — back to the published global order. */
  resetBgList(channel, type, product) {
    const params = new URLSearchParams({channel, type});
    if (product) params.set("product", product);
    return request(`/api/v3/bg-list?${params}`, {method: "DELETE"});
  },

  /**
   * Publish an arrangement for every user (admin only). Position-keyed
   * default-scene rows are retargeted server-side to follow their image.
   */
  publishBgListGlobal({channel, type, product, entries, removed}) {
    return request(`/api/v3/bg-list/global`, {
      method: "PUT",
      body: JSON.stringify({channel, type, product, entries, removed}),
    });
  },

  /**
   * Make an upload this product's FIRST background for everyone — "Save as
   * default" with a temporary upload tile. Returns its catalog `{variation}`.
   * With `variation` in place of `uploadId`: the same for a background of the
   * gallery that an admin hid on the page (the "+" of a saved link's tile).
   * With `set` (releases): only that set gets the background.
   */
  promoteBgUploadFirst({channel, type, product, uploadId, variation, set}) {
    return request(`/api/v3/bg-list/first`, {
      method: "POST",
      body: JSON.stringify({channel, type, product, uploadId, variation, set}),
    });
  },

  /**
   * Per-channel editorial settings. `stageOrder` is the admin-saved display
   * order of the channel's Stage options, or null — declared order applies.
   */
  fetchChannelSettings(channel) {
    const params = new URLSearchParams({channel});
    return request(`/api/v3/channel-settings?${params}`, {method: "GET"});
  },

  /** Save the channel-wide Stage option order (admin only). */
  putChannelSettings({channel, stageOrder}) {
    return request(`/api/v3/channel-settings`, {
      method: "PUT",
      body: JSON.stringify({channel, stageOrder}),
    });
  },

  /**
   * Hide one product, set, background or layout from the selectors, or offer
   * it again (superadmin only). `type` and `product` name the gallery of a
   * background or the page of a layout; `set` is the releases set of it. A
   * product or set change with a `type` is for that stage only.
   * Resolves to the channel's settings after the change.
   * @param {import("libs/selectorVisibility").HiddenChange} change
   */
  patchHiddenSelectorOption({channel, kind, value, hidden, type, product, set}) {
    return request(`/api/v3/channel-settings/hidden`, {
      method: "PATCH",
      body: JSON.stringify({channel, kind, value: String(value), hidden, type, product, set}),
    });
  },

  /**
   * Save the background tile order of one releases set for every user
   * (superadmin only). An empty `order` gives the set the product's order
   * again. Resolves to the channel's settings after the change.
   */
  putSetBackgroundOrder({channel, type, product, set, order}) {
    return request(`/api/v3/channel-settings/background-order`, {
      method: "PUT",
      body: JSON.stringify({channel, type, product, set, order}),
    });
  },

  /**
   * Save the Layout selector order of a product, or of every product (`*`),
   * for every stage of the set (superadmin only). The first offered layout is
   * the default layout. An empty `order` gives the wider scope's order again.
   * Resolves to the channel's settings after the change.
   * @param {{channel: string, product: string, set?: string, order: number[]}} input
   */
  putLayoutOrder({channel, product, set, order}) {
    return request(`/api/v3/channel-settings/layout-order`, {
      method: "PUT",
      body: JSON.stringify({channel, product, set, order}),
    });
  },

  /**
   * The live choice of template designs, `{rev, chosen, log, updatedAt,
   * updatedBy}` (data/templateSlots.ts). A server without the endpoint (404,
   * only while a deploy rolls out) has no choice: every slot draws its code
   * default.
   */
  async fetchTemplateSlots() {
    let doc;
    try {
      doc = await request(`/api/v3/template-slots`, {method: "GET", suppressErrorLog: true});
    } catch (error) {
      if (error instanceof HTTPError && error.status === 404) {
        console.warn("[template-slots] The server has no template slots yet: code defaults apply");
        return {rev: 0, chosen: [], log: [], updatedAt: null, updatedBy: null};
      }
      console.error("[template-slots] Could not load the template choice", error);
      throw error;
    }
    if (!doc || typeof doc.rev !== "number" || !Array.isArray(doc.chosen) || !Array.isArray(doc.log)) {
      throw new Error("Invalid /api/v3/template-slots response: rev, chosen or log missing");
    }
    return doc;
  },

  /**
   * Put a design on air in every slot that lists it (superadmin only). `rev`
   * is the revision the choice was read at; a newer one gives 409.
   */
  chooseTemplateMember({rev, member}) {
    return request(`/api/v3/template-slots`, {
      method: "PATCH",
      body: JSON.stringify({rev, member}),
    });
  },

  clearBackgroundCache: () => {
    backgroundCache.clear();
  },

  /**
   * Upload an image to the per-user gallery. Returns {id, url, ...} —
   * scene state should hold the CDN URL, never the raw blob. Content-hash
   * deduplicated server-side, so re-uploading the same file is a no-op.
   * Relative URL: the request hits the local Bun server, which routes
   * `/api/v3/uploads` to the uploads router (`src/server/api/v3/uploads.ts`).
   */
  uploadFile(file) {
    // Rebuild the FormData per attempt — a body consumed by a failed fetch
    // isn't reusable. Retries cover transient network/5xx failures only;
    // see withUploadRetries for why retrying this endpoint is safe.
    return withUploadRetries(
      () => {
        const formData = new FormData();
        formData.append("file", file);
        return request("/api/v3/uploads", {
          method: "POST",
          body: formData,
        });
      },
      {
        onRetry: (err, attempt, delayMs) =>
          console.warn(
            `[uploads] attempt ${attempt} failed (${err.message}) — retrying in ${delayMs}ms`,
            err.envelope || ""
          ),
      }
    );
  },

  // default-scenes is served in-process by the v3 Bun API (relative URLs):
  // reads are public; writes require an admin JWT (enforced server-side).
  // Optional product/type narrow the list server-side (product keeps the "*"
  // wildcard rows); omit them for the full channel list.
  getDefaultScenes(channel, {product, type} = {}) {
    const params = new URLSearchParams({channel});
    if (product) params.set("product", product);
    if (type) params.set("type", type);
    return request(`/api/v3/default-scenes?${params}`, {
      method: "GET",
      suppressErrorLog: true,
    });
  },

  setDefaultScene({
    channel,
    product,
    type,
    set,
    layout,
    background,
    variant,
    sceneData = null,
    sceneHash = null,
  }) {
    const body = {channel, product, type};
    if (sceneData !== null) body.scene_data = sceneData;
    if (sceneHash !== null) body.scene_hash = sceneHash;
    if (set !== undefined && set !== null) body.set = set;
    if (layout !== undefined && layout !== null) body.layout = layout;
    if (background !== undefined && background !== null) body.background = background;
    if (variant !== undefined && variant !== null) body.variant = variant;
    return request(`/api/v3/default-scenes`, {
      method: "PUT",
      body: JSON.stringify(body),
      suppressErrorLog: true,
    });
  },

  /**
   * Partial merge of a default-scene override (issue #528, 1d). Sends ONLY the
   * fields in `sceneData`; the server merges them onto the stored row (dot-path
   * $set), preserving unlisted fields. Use instead of setDefaultScene (PUT full
   * replace) so a cold client cache can't drop previously-saved fields.
   */
  patchDefaultScene({channel, product, type, set, layout, background, variant, sceneData}) {
    const body = {channel, product, type, scene_data: sceneData};
    if (set !== undefined && set !== null) body.set = set;
    if (layout !== undefined && layout !== null) body.layout = layout;
    if (background !== undefined && background !== null) body.background = background;
    if (variant !== undefined && variant !== null) body.variant = variant;
    return request(`/api/v3/default-scenes`, {
      method: "PATCH",
      body: JSON.stringify(body),
      suppressErrorLog: true,
    });
  },

  /**
   * Cut one banner's background to its box (POST /api/v3/backgrounds/bake).
   * Returns {url, width, height, pixelRatio}. See libs/bakedBg.ts.
   */
  bakeBackground({src, width, height, transform}) {
    return request(`/api/v3/backgrounds/bake`, {
      method: "POST",
      body: JSON.stringify({src, width, height, transform}),
      suppressErrorLog: true,
    });
  },

  deleteDefaultScene({channel, product, type, set, layout, background, variant}) {
    const body = {channel, product, type};
    if (set !== undefined && set !== null) body.set = set;
    if (layout !== undefined && layout !== null) body.layout = layout;
    if (background !== undefined && background !== null) body.background = background;
    if (variant !== undefined && variant !== null) body.variant = variant;
    return request(`/api/v3/default-scenes`, {
      method: "DELETE",
      body: JSON.stringify(body),
      suppressErrorLog: true,
    });
  },

  saveScene(params) {
    // Relative URL — handled locally by the Bun server (src/server/api/scene-save.ts).
    // Same S3 bucket as Kraken; cross-load with existing scenes.
    return request(`/scene/save?prefix=neurobaker`, {
      method: "POST",
      body: JSON.stringify(params),
    });
  },

  /**
   * Render a video on the server. A render takes minutes, longer than a proxy
   * keeps one request open (the ingress closes it after 180 s). Thus the
   * server runs it as a job: this call starts the job, polls its status, and
   * downloads the file when the job is done.
   *
   * @param {Object} params
   * @param {(status: Object) => void} [params.onStatus] - gets each job status, the last one too
   * @param {number} [params.pollMs] - time between two status requests
   * @returns {Promise<Blob>} the video file
   */
  async renderVideo({
    sceneId,
    templateId,
    profile,
    format,
    duration,
    onStatus,
    pollMs = VIDEO_JOB_POLL_MS,
  }) {
    let status = await request("/render/video/jobs", {
      method: "POST",
      body: JSON.stringify({sceneId, templateId, profile, format, duration}),
    });
    let failedPolls = 0;
    while (status.state === "queued" || status.state === "rendering") {
      onStatus?.(status);
      await new Promise((resolve) => setTimeout(resolve, pollMs));
      try {
        status = await request(`/render/video/jobs/${status.id}`);
        failedPolls = 0;
      } catch (error) {
        // The job continues on the renderer when a main pod restarts or the
        // network drops for a moment. Thus poll again after such a failure.
        const transient =
          error instanceof NetworkError || (error instanceof HTTPError && error.status >= 500);
        failedPolls++;
        if (!transient || failedPolls >= VIDEO_JOB_MAX_FAILED_POLLS) throw error;
      }
    }
    onStatus?.(status);
    if (status.state !== "done") {
      throw new Error(`Video render ${status.state}: ${status.error}`);
    }
    return request(`/render/video/jobs/${status.id}/file`, {
      responseType: "blob",
      signal: AbortSignal.timeout(120_000),
    });
  },

  /** Cancel a server video job. `keepalive` lets the request outlive a closing page. */
  cancelVideoRender(jobId) {
    return request(`/render/video/jobs/${jobId}`, {method: "DELETE", keepalive: true});
  },

  /**
   * Fetch scenes by their hashes (IDs).
   * @param {string[]} hashes - Array of scene hashes to fetch
   * @returns {Promise<Array<Object|null>>} Array of scenes in same order as input hashes
   */
  async fetchScenes(hashes) {
    const response = await request(`/scene/load/v2`, {
      method: "POST",
      body: JSON.stringify({
        scene_hashes: hashes,
        scene_prefix: "neurobaker",
      }),
    });
    return mapScenesResponse(hashes, response);
  },

  /**
   * Fetch a single scene by its hash (ID).
   * @param {string} hash - Scene hash to fetch
   * @returns {Promise<Object|null>} Scene data or null if not found
   */
  async fetchScene(hash) {
    const [scene] = await this.fetchScenes([hash]);
    return scene;
  },

  /**
   * List the current user's saved scenes (work history), newest first. Scoped
   * server-side to the authenticated caller — there is no way to request
   * another user's scenes. Returns the paginated envelope
   * `{ object, data, has_more, next_cursor }`; pass `next_cursor` back as
   * `cursor` to fetch the next page. `cursor` is opaque — do not parse it.
   * Each item carries `title`, the scene's headline, and `context`, holding
   * channel, product, and type as {id, label} snapshots. Both fields are null on
   * an older scene, so the caller falls back to the scene id.
   * @param {{cursor?: string, limit?: number}} [opts]
   * @returns {Promise<{object: string, data: Array<{sceneId: string, prefix: string, sizeBytes: number, createdAt: string, title: string|null, context: {channel: {id: string, label: string}|null, product: {id: string, label: string}|null, type: {id: string, label: string}|null}|null}>, has_more: boolean, next_cursor: string|null}>}
   */
  async listMyScenes({cursor, limit} = {}) {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    if (limit) params.set("limit", String(limit));
    const qs = params.toString();
    return request(`/scene/list${qs ? `?${qs}` : ""}`);
  },

  async fetchRendererHealth() {
    try {
      return await request("/render/health");
    } catch {
      // request() already logs the error — just return null for the UI
      return null;
    }
  },

  // Smart Layout — VLM-based compositional background positioning
  smartCropV2(sceneId, options = {}) {
    return request("/render/smart-crop", {
      method: "POST",
      body: JSON.stringify({sceneId, ...options}),
    });
  },

  /**
   * Send a scene's banner to Jet Check for AI-powered creative analysis.
   * The server renders the banner via the Playwright pool and forwards to Jet.
   * @param {string} sceneId - Saved scene id
   * @param {string} templateId - DOM id of the banner template
   * @returns {Promise<{session_id: string, url: string}>} Session data with dashboard URL
   */
  async jetCheck(sceneId, templateId) {
    const response = await fetch("/api/jet-check", {
      method: "POST",
      headers: {"Content-Type": "application/json", Accept: "application/json"},
      body: JSON.stringify({sceneId, templateId}),
      signal: AbortSignal.timeout(300_000),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Jet Check failed (${response.status}): ${text}`);
    }

    const data = await response.json();
    if (!data?.ok) {
      throw new Error(data?.error || data?.detail || "Jet Check returned an error");
    }

    return data.data;
  },
};
